// Consent plumbing.
//
// Google Consent Mode defaults are set in gatsby-ssr.js so they land before
// gtag/GTM load. Hotjar and the Facebook pixel have no consent mode, so they
// are not registered as Gatsby plugins at all — they would inject themselves
// on every page load. Instead they are loaded from here, only once the
// visitor has actually accepted.

export const CONSENT_KEY = "punde-cookie-consent";
export const HOTJAR_ID = 5346203;
export const FACEBOOK_PIXEL_ID = "554421607155052";
export const CLARITY_ID = "y26gn4945j";

const isBrowser = () => typeof window !== "undefined";

// Global Privacy Control: a browser-level opt-out signal. A visitor who has
// set it has already refused, so it outranks the regional default and means
// the banner should not ask again.
export function hasGlobalPrivacyControl() {
  return isBrowser() && window.navigator.globalPrivacyControl === true;
}

export function getStoredConsent() {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    // An explicit decision always wins. Checking storage first matters: a
    // visitor sending GPC who then opens Cookie preferences and accepts has
    // made a deliberate, informed choice, and reading the signal ahead of it
    // would leave them clicking a button that does nothing.
    if (raw === "granted" || raw === "denied") return raw;
  } catch (e) {
    // Safari in private mode throws on localStorage access.
  }
  // Nothing stored: a Global Privacy Control signal is a refusal, reported as
  // a decision so the banner does not ask again. Nothing is written to the
  // visitor's storage to record it.
  if (hasGlobalPrivacyControl()) return "denied";
  return null;
}

function storeConsent(value) {
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch (e) {
    /* nothing we can do; consent simply won't persist */
  }
}

function updateGoogleConsent(granted) {
  if (!isBrowser() || typeof window.gtag !== "function") return;
  const value = granted ? "granted" : "denied";
  // Only the signals that require consent are toggled. security_storage and
  // functionality_storage stay granted throughout — withdrawing consent must
  // not break the site or discard the reader's own checklist ticks.
  window.gtag("consent", "update", {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
    personalization_storage: value,
  });
}

function loadHotjar() {
  if (!isBrowser() || window.hj) return;
  window.hj =
    window.hj ||
    function () {
      (window.hj.q = window.hj.q || []).push(arguments);
    };
  window._hjSettings = { hjid: HOTJAR_ID, hjsv: 6 };
  const script = document.createElement("script");
  script.async = 1;
  script.src = `https://static.hotjar.com/c/hotjar-${HOTJAR_ID}.js?sv=6`;
  document.head.appendChild(script);
}

// Microsoft Clarity. Session recording and heatmaps, so it sits with Hotjar and
// the pixel rather than in gatsby-ssr.js: pasting the vendor snippet into the
// document would run it for every visitor before they decide, including in the
// 32 regions where this site defaults analytics_storage to denied.
function loadClarity() {
  if (!isBrowser() || window.clarity) return;
  /* eslint-disable */
  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i + "?ref=bwt";
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_ID);
  /* eslint-enable */
}

function loadFacebookPixel() {
  if (!isBrowser() || window.fbq) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js"
  );
  /* eslint-enable */
  window.fbq("init", FACEBOOK_PIXEL_ID);
  window.fbq("track", "PageView");
}

// Apply a consent decision: update Google Consent Mode and load (or continue
// to withhold) the trackers that cannot be gated any other way.
export function applyConsent(granted, { persist = true } = {}) {
  if (!isBrowser()) return;
  if (persist) storeConsent(granted ? "granted" : "denied");
  updateGoogleConsent(granted);
  if (granted) {
    loadHotjar();
    loadFacebookPixel();
    // Last deliberately. The other two append to <head>, which cannot fail;
    // Clarity's vendor snippet inserts before the first <script> and would
    // throw if a document had none. Kept verbatim so it stays diffable against
    // Clarity's own documentation, and ordered so that a throw here cannot
    // stop the other two from loading.
    loadClarity();
  }
}

// Re-apply a previously stored decision on load. Without this, a visitor who
// accepted on an earlier visit would silently drop back to denied.
export function restoreConsent() {
  const stored = getStoredConsent();
  if (stored === "granted") applyConsent(true, { persist: false });
  return stored;
}

// Reopening the banner. The banner copy and the privacy policy both tell
// visitors they can change their mind at any time, but a stored decision hid
// the banner forever and there was no other control — so the promise (and
// GDPR Art. 7(3), which wants withdrawal as easy as consent) went unmet.
// Anything can now request the banner back by calling this.
export const CONSENT_REOPEN_EVENT = "punde:consent-reopen";

export function reopenConsentBanner() {
  if (!isBrowser()) return;
  window.dispatchEvent(new CustomEvent(CONSENT_REOPEN_EVENT));
}

// Withdrawing after accepting is not just a stored-value change: Hotjar, the
// Facebook pixel and Clarity are already in the page and cannot be unloaded.
// Consent Mode is updated and the decision stored first, then the page is
// reloaded so the withdrawn trackers are genuinely gone.
export function withdrawConsent() {
  if (!isBrowser()) return;
  const wasGranted = getStoredConsent() === "granted";
  applyConsent(false);
  if (wasGranted) window.location.reload();
}

// Custom events. Gated on a stored grant rather than on gtag being present:
// Consent Mode would let an event through with analytics_storage denied, which
// is not what the banner promised. No identifiers are ever passed — these carry
// counts and a path, nothing about the person.
export function trackEvent(name, params = {}) {
  if (!isBrowser()) return;
  // An explicit refusal, or a browser-level one, stops the event outright.
  if (getStoredConsent() === "denied") return;
  if (typeof window.gtag !== "function") return;
  // Otherwise defer to Consent Mode, exactly as the pageview does. Requiring
  // a stored grant here would have measured events against a smaller
  // population than the pageviews they are divided by.
  window.gtag("event", name, params);
}

// Facebook's pixel tracks SPA route changes itself only if it is loaded.
export function trackPageView() {
  if (isBrowser() && typeof window.fbq === "function") {
    window.fbq("track", "PageView");
  }
}
